import { find, filter, groupBy, isEmpty, keys, forOwn, get } from 'lodash';
import { operators } from 'utils';
import { cleanObject } from './general';
import { getQuickFilterFromName } from './dataMappers';
import { parseDateFormat } from './formatFns';

export const getContainsObj = ({ field, value }) => ({
  [field]: {
    contains: value,
  },
});

export const getAndEqObj = ({ fields, values }) => {
  if (fields.length !== values.length) {
    return;
  }

  return {
    and: fields.map((field, index) => {
      return { [field]: { eq: values[index] } };
    }),
  };
};

export const getOperator = ({ formattedFilter }) => {
  const formattedFilterKey = keys(formattedFilter)[0];
  if (formattedFilterKey === 'or') {
    return 'or';
  } else if (formattedFilterKey === 'and') {
    return 'and';
  }
  return null;
};

export const getEqObj = ({ field, value }) => ({
  [field]: {
    eq: value,
  },
});

export const getInObj = ({ field, value }) => ({
  [field]: {
    in: value,
  },
});

const isAndOperator = ({ formattedFilter }) => get(formattedFilter, 'and');

const getFilterArray = ({ formattedFilter }) => {
  if (isEmpty(formattedFilter)) {
    return [];
  }
  if (Array.isArray(formattedFilter)) {
    return formattedFilter;
  }
  if (isAndOperator({ formattedFilter })) {
    return formattedFilter?.and || [];
  }
  return [formattedFilter];
};

export const mergeFormattedFilters = ({ first, second, third, fourth }) => {
  return cleanObject({
    and: [
      ...getFilterArray({ formattedFilter: first }),
      ...getFilterArray({ formattedFilter: second }),
      ...getFilterArray({ formattedFilter: third }),
      ...getFilterArray({ formattedFilter: fourth }),
    ],
  });
};

const removeQuickFilters = ({ cascadingFiltersClone }) =>
  cascadingFiltersClone.filter(({ field }) => field !== 'quickFilters');

const getQuickFiltersObj = ({ cascadingFiltersClone, features }) => {
  let filtersFormattedObj = [];
  const quickFilters = cascadingFiltersClone.filter(({ field }) => field === 'quickFilters');

  quickFilters.forEach((quickFilter) => {
    if (quickFilter.isChecked) {
      const quickFilterFormatted = getQuickFilterFromName({
        filterName: quickFilter.value,
        features,
      });
      filtersFormattedObj = [...filtersFormattedObj, quickFilterFormatted];
    }
  });

  return { or: filtersFormattedObj };
};

export const cascadingFiltersFormatter = ({ cascadingFilters, features }) => {
  if (isEmpty(cascadingFilters) || !!cascadingFilters.find((f) => f.isCascadingFilterOff)) {
    return {};
  }

  let filtersFormatted = [];
  let cascadingFiltersClone = [...cascadingFilters];
  const isQuickFiltersIncluded = !!find(cascadingFilters, { field: 'quickFilters' });

  if (isQuickFiltersIncluded) {
    const quickFiltersFormattedObj = getQuickFiltersObj({
      cascadingFiltersClone,
      features,
    });
    filtersFormatted = [...filtersFormatted, quickFiltersFormattedObj];
    cascadingFiltersClone = removeQuickFilters({ cascadingFiltersClone });
  }

  const filteredIsCheckedTrue = filter(cascadingFiltersClone, { isChecked: true });
  const filtersGrouped = groupBy(filteredIsCheckedTrue, 'field');
  forOwn(filtersGrouped, (value, field) => {
    if (value.length > 1) {
      const values = value.map(({ value }) => value);
      filtersFormatted = [...filtersFormatted, getInObj({ field, value: values })];
    } else {
      filtersFormatted.push(getEqObj({ field, value: value[0].value }));
    }
  });
  return cleanObject({ and: filtersFormatted });
};

export const getNumericInlineValue = (condition, displayFormat) => {
  const [conditionsArray] = Object.entries(condition);
  const operatorName = conditionsArray[0];
  const value = conditionsArray[1];
  let inlineValue = '';

  if (operatorName !== 'eq') {
    operators
      .filter((operator) => operator.type === 'number')
      .forEach((operator) => {
        if (operator.value === operatorName) {
          inlineValue += operator.label;
        }
      });
  }

  if (value && displayFormat) {
    inlineValue += parseDateFormat({
      dateValue: value,
      dateFormat: displayFormat,
      type: 'date',
    })?.dateValue;
  } else if (operatorName === 'all' && Array.isArray(value)) {
    inlineValue += value.join(' & ');
  } else if (operatorName === 'nAll' && Array.isArray(value)) {
    inlineValue += value.map((v) => '!=' + v).join(' & ');
  } else if ((operatorName === 'nin' || operatorName === 'notContains') && Array.isArray(value)) {
    inlineValue += '!=' + value.join(',');
  } else if (operatorName === 'mContains' && Array.isArray(value)) {
    inlineValue += value.join(', ');
  } else {
    inlineValue += value;
  }

  return inlineValue;
};
